import { computed, ref } from "vue";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
}

const deferredInstallPrompt = ref<BeforeInstallPromptEvent | null>(null);
const isInstallPromptInitialized = ref(false);
const isInstalling = ref(false);
let listenersAttached = false;

const isStandaloneDisplayMode = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    Boolean(window.matchMedia?.("(display-mode: standalone)").matches) ||
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone)
  );
};

const syncInstallAvailability = (): void => {
  isInstallPromptInitialized.value =
    !isStandaloneDisplayMode() && deferredInstallPrompt.value !== null;
};

export const isKioskInstallCapturePath = (pathname: string): boolean => {
  const pathSegments = pathname.split("/").filter(Boolean);

  if (pathSegments[0] !== "kiosk") {
    return false;
  }

  if (pathSegments.length === 1) {
    return true;
  }

  if (pathSegments.length === 2) {
    return true;
  }

  return pathSegments.length === 3 && pathSegments[2] === "splash";
};

const attachInstallListeners = (): void => {
  if (listenersAttached || typeof window === "undefined") {
    return;
  }

  listenersAttached = true;

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredInstallPrompt.value = event as BeforeInstallPromptEvent;
    syncInstallAvailability();
  });

  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt.value = null;
    syncInstallAvailability();
  });
};

export const initializePwaInstall = (): void => {
  attachInstallListeners();
  syncInstallAvailability();
};

export const usePwaInstall = () => {
  initializePwaInstall();

  const canInstall = computed(() => isInstallPromptInitialized.value && !isInstalling.value);

  const promptInstall = async (): Promise<boolean> => {
    const promptEvent = deferredInstallPrompt.value;
    if (!promptEvent || isInstalling.value) {
      return false;
    }

    isInstalling.value = true;

    try {
      await promptEvent.prompt();
      const outcome = await promptEvent.userChoice;
      deferredInstallPrompt.value = null;
      syncInstallAvailability();
      return outcome.outcome === "accepted";
    } finally {
      isInstalling.value = false;
    }
  };

  return {
    canInstall,
    isInstalling: computed(() => isInstalling.value),
    promptInstall,
  };
};
